import { useCallback, useMemo } from 'react';
import Decimal from 'decimal.js';
import type { Transaction } from '@/api/rpc/hooks/ship-and-debit/useE844sdTransactions';
import {
  useListTransactions,
  useStageInternalTransactions,
  useUnstageInternalTransactions,
} from '@/api/rpc/hooks/ship-and-debit';
import type { MutationCallbacks } from './useWorkbench';

export function useInternalWorkbench(
  params: {
    limit?: number;
    offset?: number;
    columnFilters?: Record<string, string[]>;
    textFilters?: Record<string, string[]>;
    dateFilters?: Record<string, string[]>;
    numericFilters?: Record<string, string[]>;
    hasComments?: boolean;
    sortBy?: string;
    sortDirection?: 'asc' | 'desc';
  } = {},
) {
  const {
    transactions: stagedTransactions,
    pagination,
    isLoading,
    isFetching,
  } = useListTransactions({
    statuses: ['staged_internal'],
    ...params,
  });
  const stageMutation = useStageInternalTransactions();
  const unstageMutation = useUnstageInternalTransactions();

  const workbenchIds = useMemo(
    () => new Set(stagedTransactions.map((t) => t.id)),
    [stagedTransactions],
  );

  const addToWorkbench = useCallback(
    (
      newTransactions: Transaction | Transaction[],
      options?: MutationCallbacks,
    ) => {
      const toAdd = Array.isArray(newTransactions)
        ? newTransactions
        : [newTransactions];
      const ids = toAdd.map((t) => t.id);
      if (ids.length > 0) {
        stageMutation.mutate(ids, {
          onSuccess: options?.onSuccess,
          onError: options?.onError,
        });
      }
    },
    [stageMutation],
  );

  const removeFromWorkbench = useCallback(
    (transactionIds: string | string[], options?: MutationCallbacks) => {
      const ids = Array.isArray(transactionIds)
        ? transactionIds
        : [transactionIds];
      if (ids.length > 0) {
        unstageMutation.mutate(ids, {
          onSuccess: options?.onSuccess,
          onError: options?.onError,
        });
      }
    },
    [unstageMutation],
  );

  const clearWorkbench = useCallback(() => {
    unstageMutation.mutate({ all: true });
  }, [unstageMutation]);

  const isInWorkbench = useCallback(
    (transactionId: string) => {
      return workbenchIds.has(transactionId);
    },
    [workbenchIds],
  );

  const totalClaimAmount = useMemo(
    () =>
      stagedTransactions
        .reduce((sum, t) => {
          const raw = (t as unknown as Record<string, unknown>)['claim_amount'];
          const amount = new Decimal(raw != null ? Number(raw) : 0);
          return sum.plus(amount);
        }, new Decimal(0))
        .toNumber(),
    [stagedTransactions],
  );

  return {
    transactions: stagedTransactions,
    pagination,
    workbenchIds,
    isLoading,
    isFetching,
    addToWorkbench,
    /** True while a stage-internal mutation is in flight. */
    isAddingToWorkbench: stageMutation.isPending,
    removeFromWorkbench,
    clearWorkbench,
    isInWorkbench,
    totalClaimAmount,
  };
}
